import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
export const API_BASE = `${BACKEND_URL}/api`;

/**
 * Axios instance con:
 *  - withCredentials: invia/riceve cookie httpOnly (ht_access, ht_refresh)
 *  - X-Requested-With: header custom obbligatorio per la CSRF protection lato server
 *    (viene bloccato dai browser sui cross-origin POST → protegge da CSRF classici)
 *
 * NOTA sulla migrazione:
 *  - I cookie httpOnly sono l'autenticazione primaria (mitigazione XSS: JS non può leggerli).
 *  - Manteniamo il fallback su localStorage/Authorization header per una release di transizione:
 *    utenti loggati prima del deploy continueranno a funzionare senza dover rifare login.
 *  - Non appena tutti i client attivi avranno ruotato le sessioni (dopo ~30 giorni),
 *    si potrà rimuovere il ramo Authorization dagli interceptor e da setAuth.
 */
export const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
  headers: { "X-Requested-With": "XMLHttpRequest" },
});

// ---- Storage helpers (solo per transizione + info utente non sensibile) ----
export const setAuth = (accessToken, user, refreshToken) => {
  // Conserviamo lo user in localStorage per hydration rapida (nessuna info segreta).
  localStorage.setItem("ht_user", JSON.stringify(user));
  // I token restano in localStorage ANCHE nella modalità cookie per retro-compat:
  //   - se il browser blocca cookie di terze parti / dominio diverso,
  //     il fallback Authorization: Bearer continua a funzionare.
  //   - il backend accetta entrambe le strategie (dual-mode).
  if (accessToken) localStorage.setItem("ht_token", accessToken);
  if (refreshToken) localStorage.setItem("ht_refresh", refreshToken);
  // Un login "vero" (email/password o Google) sovrascrive un eventuale flag di demo
  // rimasto in localStorage da una precedente sessione "Esplora" sulla landing.
  localStorage.removeItem("ht_demo_mode");
};

export const clearAuth = () => {
  localStorage.removeItem("ht_token");
  localStorage.removeItem("ht_user");
  localStorage.removeItem("ht_refresh");
  localStorage.removeItem("ht_demo_mode");
};

export const getUser = () => {
  const s = localStorage.getItem("ht_user");
  return s ? JSON.parse(s) : null;
};

// ---- Request interceptor: attach Bearer come fallback se il cookie non è disponibile ----
// In modalità demo, bloccare a monte tutte le richieste "modificanti" (POST/PUT/PATCH/DELETE)
// tranne un piccolo elenco di whitelist (login/logout/refresh, tracking, ecc.). L'utente
// demo è vetrina read-only: le mutazioni aprono il DemoActionDialog invece di eseguirsi.
const DEMO_WRITE_METHODS = new Set(["post", "put", "patch", "delete"]);
const DEMO_WRITE_WHITELIST = [
  "/auth/login", "/auth/login/2fa", "/auth/refresh", "/auth/register",
  "/auth/demo-login", "/auth/logout",
  "/auth/forgot-password", "/auth/reset-password",
  "/auth/passkey-prompt/dismiss", // demo skippa già l'enroll, ma per sicurezza
  "/track", // eventuali eventi analytics
];

/**
 * Segnala una richiesta bloccata in demo. Il DemoActionProvider registra qui
 * un handler globale (fuori da React tree) per aprire il modale con la label.
 */
let __demoBlockedHandler = null;
export const registerDemoBlockedHandler = (fn) => { __demoBlockedHandler = fn; };

api.interceptors.request.use((cfg) => {
  const token = localStorage.getItem("ht_token");
  if (token && !cfg.headers?.Authorization) {
    cfg.headers = cfg.headers || {};
    cfg.headers.Authorization = `Bearer ${token}`;
  }
  // Blocco write in demo mode.
  try {
    if (localStorage.getItem("ht_demo_mode") === "1") {
      const method = (cfg.method || "get").toLowerCase();
      const url = cfg.url || "";
      const isWrite = DEMO_WRITE_METHODS.has(method);
      const isAllowed = DEMO_WRITE_WHITELIST.some((p) => url.includes(p));
      if (isWrite && !isAllowed) {
        if (typeof __demoBlockedHandler === "function") {
          try { __demoBlockedHandler({ url, method }); } catch {}
        }
        const err = new Error("Blocked in demo mode");
        err.isDemoBlocked = true;
        err.config = cfg;
        return Promise.reject(err);
      }
    }
  } catch {}
  return cfg;
});

// ---- Response interceptor: transparent 401 → refresh → retry ----
let refreshInFlight = null;

async function performRefresh() {
  // 1) Prima prova senza body: il backend leggerà dal cookie ht_refresh
  //    (opzione preferita: il refresh non viene mai esposto a JS).
  // 2) Se non c'è cookie o fallisce, cade sul refresh_token in localStorage (legacy).
  try {
    const resp = await axios.post(
      `${API_BASE}/auth/refresh`,
      {},
      {
        withCredentials: true,
        headers: { "X-Requested-With": "XMLHttpRequest" },
      },
    );
    const newToken = resp.data?.access_token;
    if (newToken) {
      localStorage.setItem("ht_token", newToken);
      return newToken;
    }
  } catch (_) {
    // fallthrough al fallback legacy
  }
  const refresh = localStorage.getItem("ht_refresh");
  if (!refresh) throw new Error("no-refresh-token");
  const resp = await axios.post(
    `${API_BASE}/auth/refresh`,
    { refresh_token: refresh },
    { withCredentials: true, headers: { "X-Requested-With": "XMLHttpRequest" } },
  );
  const newToken = resp.data?.access_token;
  if (!newToken) throw new Error("refresh-no-token");
  localStorage.setItem("ht_token", newToken);
  return newToken;
}

api.interceptors.response.use(
  (res) => res,
  async (error) => {
    const original = error.config || {};
    const status = error.response?.status;
    // Non ritentare per login/refresh/register/session per non causare loop infiniti
    const url = original.url || "";
    const skip = [
      "/auth/login", "/auth/login/2fa", "/auth/refresh",
      "/auth/register", "/auth/demo-login",
      "/auth/forgot-password", "/auth/reset-password",
    ].some((p) => url.includes(p));
    if (status !== 401 || original._retry || skip) {
      return Promise.reject(error);
    }
    original._retry = true;
    try {
      if (!refreshInFlight) refreshInFlight = performRefresh().finally(() => { refreshInFlight = null; });
      const newToken = await refreshInFlight;
      original.headers = original.headers || {};
      original.headers.Authorization = `Bearer ${newToken}`;
      return api(original);
    } catch (_) {
      // refresh fallito → forza logout locale
      clearAuth();
      if (typeof window !== "undefined" && !window.location.pathname.startsWith("/login")) {
        window.location.href = "/login";
      }
      return Promise.reject(error);
    }
  }
);

// Iter209au — Etichette canoniche dei ruoli.
// Fonte di verità unica: TUTTI i componenti (InviteLinkPanel,
// JoinInvite, Profile, AdminFeedback, PracticeDetail, ServicesAdmin,
// Users, ecc.) devono importare da qui invece di ridefinire mappe
// locali. Se serve aggiornare un'etichetta, modificala solo qui.
export const ROLE_LABELS = {
  admin: "Admin",
  professional: "Professionista",
  // Iter209bm — Label operatori usa i valori interni come display name
  // (senza traduzione italiana). Fonte di verità unica per tutta la UI.
  tech_operator: "Tech Operator",
  site_operator: "Site Operator",
  desk_operator: "Desk Operator",
  accounting_operator: "Accounting Operator",
};

// Helper per ricavare l'etichetta con fallback allo slug tecnico.
export const roleLabel = (r) => ROLE_LABELS[r] || r || "";
